import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NewplaAiUserMemoryDto, NewplaAiUserSettingApi } from './newpla-ai-user-setting.api';

/**
 * 개인 AI 설정. 채팅 전용이 아니라 AI 공통이다. 채팅 화면은 오른쪽 위 "설정"에서 이 컴포넌트를 연다
 * - 지금 섹션: 내 기억(보기, 직접 추가, 수정, 지우기, 모두 지우기). AI가 대화 중 저장한 기억도 여기 보인다
 * - 나중에 다른 개인 설정을 섹션으로 붙인다
 *
 * 예: <newpla-ai-user-setting />
 */
@Component({
  selector: 'newpla-ai-user-setting',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './newpla-ai-user-setting.component.html',
  styleUrl: './newpla-ai-user-setting.component.scss',
})
export class NewplaAiUserSettingComponent implements OnInit {

  private api = inject(NewplaAiUserSettingApi);

  memories: NewplaAiUserMemoryDto[] = [];
  limits = { maxItems: 0, maxLength: 0 };
  loading = false;
  busy = false;
  errorText = '';

  newContent = '';
  editingId: string | null = null;
  editContent = '';

  ngOnInit(): void {
    this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    try {
      const res = await this.api.findMyMemories();
      this.memories = res.memories;
      this.limits = res.limits;
    } catch (e) {
      this.errorText = errorMessage(e);
    } finally {
      this.loading = false;
    }
  }

  get full(): boolean {
    return this.limits.maxItems > 0 && this.memories.length >= this.limits.maxItems;
  }

  async add(): Promise<void> {
    const content = this.newContent.trim();
    if (!content || this.busy) {
      return;
    }
    await this.run(async () => {
      const saved = await this.api.addMemory(content);
      this.memories = [...this.memories, saved];
      this.newContent = '';
    });
  }

  startEdit(memory: NewplaAiUserMemoryDto): void {
    this.editingId = memory.id;
    this.editContent = memory.content;
    this.errorText = '';
  }

  cancelEdit(): void {
    this.editingId = null;
  }

  async saveEdit(memory: NewplaAiUserMemoryDto): Promise<void> {
    const content = this.editContent.trim();
    if (!content || this.busy) {
      return;
    }
    if (content === memory.content) {
      this.editingId = null;
      return;
    }
    await this.run(async () => {
      const saved = await this.api.updateMemory(memory.id, content);
      this.memories = this.memories.map(m => (m.id === saved.id ? saved : m));
      this.editingId = null;
    });
  }

  async remove(memory: NewplaAiUserMemoryDto): Promise<void> {
    if (!confirm('이 기억을 지울까요?')) {
      return;
    }
    await this.run(async () => {
      await this.api.deleteMemory(memory.id);
      this.memories = this.memories.filter(m => m.id !== memory.id);
    });
  }

  async removeAll(): Promise<void> {
    if (!this.memories.length || !confirm(`기억 ${this.memories.length}개를 모두 지울까요? 되돌릴 수 없습니다.`)) {
      return;
    }
    await this.run(async () => {
      await this.api.deleteAllMemories();
      this.memories = [];
    });
  }

  onEditKeydown(event: KeyboardEvent, memory: NewplaAiUserMemoryDto): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.saveEdit(memory);
    } else if (event.key === 'Escape') {
      this.cancelEdit();
    }
  }

  onAddKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.add();
    }
  }

  trackById(_: number, m: NewplaAiUserMemoryDto): string {
    return m.id;
  }

  private async run(task: () => Promise<void>): Promise<void> {
    this.busy = true;
    this.errorText = '';
    try {
      await task();
    } catch (e) {
      this.errorText = errorMessage(e);
    } finally {
      this.busy = false;
    }
  }
}

function errorMessage(e: unknown): string {
  if (e instanceof HttpErrorResponse) {
    const body = e.error;
    const msg = typeof body === 'string' ? body : body?.message ?? body?.msg;
    if (msg) {
      return msg;
    }
  }
  return '처리하지 못했습니다. 다시 시도하세요.';
}
